import React, { useState, useEffect } from "react";
import { Table, Button, Input, Row, Col, Card, notification } from "antd";
import { useParams } from "react-router-dom";
import { formatKey } from "./../Service";
import { TenantService } from "../../../Services";
import isEqual from "lodash/isEqual";

export const ClientReportColumns = () => {
	const { tenantName } = useParams();

	const [oldColumnsData, setOldColumnsData] = useState([]);
	const [columnsData, setColumnsData] = useState([]);
	const [deletedRows, setDeletedRows] = useState([]);

	useEffect(() => {
		const fetchTenantConfig = async () => {
			try {
				const { data } = await TenantService.getTenantClientReportColumns({ tenantName });
				// map backend _id → id so every row has a single `id` field
				const fetched = data.clientReportColumns.map(col => ({
					...col,
					id: col._id ?? col.id
				}));
				setColumnsData(fetched);
				setOldColumnsData(fetched);
			} catch (error) {
				notification.error({ message: "Error fetching Report Columns" });
				console.error("Error fetching tenant config:", error);
			}
		};
		fetchTenantConfig();
	}, [tenantName]);

	// Add a blank row with a unique timestamp-based id
	const addRow = () => setColumnsData(cols => [{ key: "", name: "", id: Date.now() }, ...cols]);

	// Delete by `id`
	const deleteRow = id => {
		const rowToDelete = columnsData.find(row => row.id === id);
		setDeletedRows(dr => [...dr, rowToDelete]);
		setColumnsData(cols => cols.filter(row => row.id !== id));
	};

	// Undo last delete
	const undoDelete = () => {
		if (!deletedRows.length) return;
		const restored = deletedRows[deletedRows.length - 1];
		setColumnsData(cols => [...cols, restored]);
		setDeletedRows(dr => dr.slice(0, -1));
	};

	// Update a single cell by `id`
	const updateRow = (id, field, value) => {
		setColumnsData(cols => cols.map(row => (row.id === id ? { ...row, [field]: value } : row)));
	};

	const columns = [
		{
			title: "Column Key (DB Key)",
			dataIndex: "key",
			render: (text, record) => (
				<Input
					value={text}
					onChange={e => updateRow(record.id, "key", e.target.value)}
					placeholder="Enter column key"
				/>
			)
		},
		{
			title: "Column Name (Display Name)",
			dataIndex: "name",
			render: (text, record) => (
				<Input
					value={text}
					onChange={e => updateRow(record.id, "name", e.target.value)}
					placeholder="Enter display name"
				/>
			)
		},
		{
			title: "Action",
			render: (_, record) => (
				<Button onClick={() => deleteRow(record.id)} type="link">
					Delete
				</Button>
			)
		}
	];

	return (
		<Card title="Client Report Columns">
			<Row gutter={[0, 16]}>
				<Col span={24}>
					<Row justify="space-between">
						<Button type="primary" onClick={addRow}>
							Add Column
						</Button>
						<Button
							onClick={() =>
								setColumnsData(cols => cols.map(row => ({ ...row, name: formatKey(row.key) })))
							}
							disabled={!columnsData.length}
						>
							Auto Generate Display Name
						</Button>
						<Button onClick={undoDelete} disabled={!deletedRows.length}>
							Undo Delete
						</Button>
					</Row>
				</Col>

				<Col span={24}>
					<Table
						dataSource={columnsData}
						columns={columns}
						rowKey="id"
						pagination={false}
						bordered
						scroll={{ y: 400 }}
					/>
				</Col>

				<Col span={24}>
					<Row justify="end">
						<Button
							type="primary"
							disabled={isEqual(
								[...columnsData].sort((a, b) => a.id - b.id),
								[...oldColumnsData].sort((a, b) => a.id - b.id)
							)}
							onClick={async () => {
								try {
									const { data } = await TenantService.updateTenantClientReportColumns({
										tenantName,
										clientReportColumns: columnsData.map(({ key, name }) => ({ key, name }))
									});
									// remap incoming ids again
									const updated = data.clientReportColumns.map(col => ({
										...col,
										id: col._id ?? col.id
									}));
									setColumnsData(updated);
									setOldColumnsData(updated);
									notification.success({ message: "Columns updated successfully" });
								} catch (error) {
									notification.error({ message: "Error updating columns" });
									console.error("Error updating columns:", error);
								}
							}}
						>
							Update Columns
						</Button>
					</Row>
				</Col>
			</Row>
		</Card>
	);
};

export default ClientReportColumns;
